import * as _ from "lodash";
import moment from "moment";
import { get } from "svelte/store";

// stores
import CartStore from "@retailer/svelte/stores/CartStore";
import CartSuccessStore from "@retailer/stores/CartSuccessStore";

// services
import { clearCacheForInventory } from "@retailer/services/pricing_data_service";
import { EventTracking } from "@shared/services/event_tracking/events";
import { CartApiMetadataEntity } from "@shared/services/event_tracking/entities";
import { isAddOnOrder, meetsOrderMinimum } from "@retailer/services/cart_order_service";
import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";

const { trackEvent } = EventTracking;

const service = function ($rootScope, $http, $q, Flash, Tracking, CartApiCallbacks, Retailers) {
  const runValidity = function () {
    return $http({
      method: "GET",
      url: "/api/retailer/cart/validity",
    });
  };

  const runSavedProducts = function (callback) {
    if (!$rootScope.cart) {
      $rootScope.cart = {};
    }
    $rootScope.savedProducts = {};
    return $http({
      method: "GET",
      url: "/api/retailer/saved_products",
    }).then(callback, function error(res) {
      let message = "Sorry, we had a problem - please try again or contact support.";
      Flash.setMessage(message, true);
      return res;
    });
  };

  const batchUpdateOrderWarehouses = async function (data) {
    return $http({
      method: "PUT",
      url: "/api/retailer/order_warehouses/batch_update",
      data: { updates: data },
    });
  };

  const batchUpdateOrders = async function (data) {
    return $http({
      method: "PUT",
      url: "/api/retailer/orders/batch_update",
      data: { updates: data },
    });
  };

  const remove = function (cartProduct, callback, skipPlaceholder) {
    $rootScope.showLoading = true;
    const deleteParams = _.merge(
      _.pick(
        cartProduct,
        "location",
        "collection_id",
        "collection_segment_id",
        "collection_schedule_id",
        "collection_entry_id",
      ),
      Tracking.filterParams("previous"),
    );
    $rootScope.removedFromOrder = $rootScope.cart.orders.find(o => {
      return o.distributor.id == cartProduct.distributor_id;
    });

    let params = { cart_product: deleteParams };
    const promise = $http({
      method: "DELETE",
      url: "/api/retailer/cart_products/" + cartProduct.id,
      params: params,
    });

    promise
      .then(load)
      .then(
        function success(res) {
          CartApiCallbacks.prepInventory();

          if (!cartProduct.saved && !skipPlaceholder) {
            CartApiCallbacks.addPlaceholder(cartProduct);
            CartStore.update(n => $rootScope.cart);
          }
          if (callback) {
            callback(null, res.data);
          }
          $rootScope.showLoading = false;
        },
        function error(res) {
          $rootScope.showLoading = false;
          let message =
            ((res || {}).data || {}).errors || "Sorry, we had a problem - please try again.";
          Flash.setMessage(message, true);
          if (callback) {
            callback(res.data, null);
          }
        },
      )
      .then(
        _.bind(CartApiCallbacks.runCartChangeEvent, {
          cartProduct: cartProduct,
          skipFlash: !skipPlaceholder,
        }),
      );
    return promise;
  };

  const add = function (product, callback, skipFlash) {
    $rootScope.showLoading = true;

    let params = {};

    let promise = $http({
      method: "POST",
      url: "/api/retailer/cart_products",
      params: params,
      data: { cart_product: product },
    });

    promise = promise
      .then(load)
      .then(
        function success(res) {
          CartStore.update(n => $rootScope.cart);
          CartApiCallbacks.prepInventory();
          if (callback) callback(null, res.data);
        },
        function error(res) {
          console.error("error res from cart add: ", res);
          $rootScope.showLoading = false;
          let message =
            ((res || {}).data || {}).errors || "Sorry, we had a problem - please try again.";
          Flash.setMessage(message, true);
          if (callback) callback(res.data, null);
        },
      )
      .then(
        _.bind(CartApiCallbacks.runCartChangeEvent, {
          cartProduct: product,
          skipFlash: skipFlash,
          segmentEvent: "Product Added",
        }),
      );

    return promise;
  };

  const updateQuantity = function (cartProduct, callback) {
    let updatePromise = $q.when();
    $rootScope.showLoading = true;
    cartProduct.updating = true;
    if (cartProduct.case_quantity == undefined) {
      delete cartProduct.case_quantity;
    }
    if (cartProduct.unit_quantity == undefined) {
      delete cartProduct.unit_quantity;
    }
    cartProduct = _.merge(cartProduct, Tracking.filterParams("previous"));

    let params = {};

    updatePromise.then(function () {
      const res = $http({
        method: "PUT",
        url: "/api/retailer/cart_products/" + cartProduct.id,
        params: params,
        data: { cart_product: cartProduct },
      });

      res
        .then(load)
        .then(
          function success(res) {
            cartProduct.updating = false;
            CartApiCallbacks.prepInventory();

            if (callback) callback(null, res.data);
          },
          function error(res) {
            $rootScope.showLoading = false;
            cartProduct.errorRes = (res.data || {}).errors;
            if (callback) callback(res.data, null);
          },
        )
        .then(_.bind(CartApiCallbacks.runCartChangeEvent, { cartProduct: cartProduct }));
      updatePromise = res;
    });

    return updatePromise;
  };

  const updateDeliveryOrPickupLocation = function (cartProduct, callback) {
    let updatePromise = $q.when();
    $rootScope.showLoading = true;
    cartProduct.updating = true;
    cartProduct = _.merge(cartProduct, Tracking.filterParams("previous"));

    let params = {};

    updatePromise.then(function () {
      const res = $http({
        method: "PUT",
        url: "/api/retailer/cart_products/" + cartProduct.id,
        params: params,
        data: { cart_product: cartProduct },
      });

      res
        .then(load)
        .then(
          function success(res) {
            clearCacheForInventory(cartProduct.inventory_id);
            cartProduct.updating = false;

            if (callback) callback(null, res.data);
          },
          function error(res) {
            $rootScope.showLoading = false;
            cartProduct.errorRes = (res.data || {}).errors;
            if (callback) callback(res.data, null);
          },
        )
        .then(_.bind(CartApiCallbacks.runCartChangeEvent, { cartProduct: cartProduct }));
      updatePromise = res;
    });

    return updatePromise;
  };

  const submitForApproval = function (managerIds, callback) {
    $http({
      method: "POST",
      url: "/api/retailer/cart/submit_for_approval",
      data: { manager_ids: managerIds },
    }).then(
      function success(res) {
        $rootScope.cart.submitted_for_approval_at = moment();
        if (callback) callback(null, res.data);
      },
      function error(res) {
        if (callback) callback(res.data, null);
      },
    );
  };

  const submitForPunchout = function () {
    return $http({
      method: "POST",
      url: "/api/retailer/cart/submit_for_punchout",
    });
  };

  const loadCount = function () {
    return $http({
      method: "GET",
      url: "/api/retailer/cart/total",
    }).then(function success(res) {
      $rootScope.cartTotal = res.data.total_items;
    });
  };

  const load = function (response) {
    $rootScope.cart = response.data;
    $rootScope.cartTotal = $rootScope.cart && $rootScope.cart.total_items;

    if ($rootScope.cartTotal) {
      CartApiCallbacks.calculateDollarMinimums();
    }
    const promises = [];
    promises.push(runValidity().then(CartApiCallbacks.validityCallback));
    if (window.location.pathname === "/cart") {
      promises.push(runSavedProducts().then(CartApiCallbacks.savedProductsCallback));
    }

    $rootScope.cart.orders?.forEach(order => {
      if (order && order.distributor.tabc_delivery_minimum_order_blocking) {
        promises.push(
          getTabcSpiritDeliveryMinimum(order.id).then(res =>
            CartApiCallbacks.deliveryMinimumCallback(res, order),
          ),
        );
      }
      if (
        stagedRolloutCheck("marketplace__fulfillment_methods") &&
        order &&
        order.distributor.uses_wine_cellar
      ) {
        promises.push(
          getMinimumOrderQuantity(order.id).then(res =>
            CartApiCallbacks.minimumOrderQuantityCallback(res, order),
          ),
        );
      }
    });

    const resPromise = $q.all(promises);

    if ($rootScope.cart) {
      CartApiCallbacks.prepDeliveryDates();
    }
    $rootScope.showLoading = false;

    return resPromise.then(function () {
      CartStore.update(n => $rootScope.cart);
      return response;
    });
  };

  const loadCart = function (params) {
    params = params || {};
    return $http({
      method: "GET",
      url: "/api/retailer/cart",
      params: params,
    }).then(load);
  };

  const submitIndividualOrderPromise = async orderId => {
    return $http({
      method: "POST",
      url: "/api/retailer/cart/submit_individual_order",
      data: {
        order_id: orderId,
      },
    });
  };

  const submitIndividualOrder = async orderId => {
    const res = await submitIndividualOrderPromise(orderId).catch(err => {
      throw err;
    });
    afterSubmit(res, true);

    // fetch the new Cart created by submit_individual_order to hold the unsubmitted orders
    loadCart();
  };

  const submitPromise = async () => {
    return $http({
      method: "POST",
      url: "/api/retailer/cart/submit",
    });
  };

  const submit = async () => {
    const res = await submitPromise().catch(err => {
      throw err;
    });

    afterSubmit(res, false);

    // fetch the new Cart created if there were blocked orders
    loadCart();
  };

  const afterSubmit = (res, individualOrder) => {
    if (!res) return;

    const cartStore = get(CartStore);
    const submittedCart = res.data;
    const orderGroupEntity = new CartApiMetadataEntity({
      type: "order_group",
      id: submittedCart.id,
    });

    for (let order of submittedCart.orders || []) {
      if (order) {
        const cartOrder = cartStore.orders.find(cartOrder => order.id === cartOrder.id);
        const cartProductsEntity = new CartApiMetadataEntity({
          type: "cart_products",
          cart_products: order.products.map(product => {
            return {
              type: "cart_product",
              productId: product.product_id,
              productLineId: product.product_line.id,
              unitQuantity: product.unit_quantity,
              caseQuantity: product.case_quantity,
              distributor: order.distributor.id,
              orderId: order?.id,
              orderGroupId: submittedCart.id,
              stockLevelAtSubmission: product.stock_level_at_submission,
            };
          }),
        });
        const distributorEntity = new CartApiMetadataEntity({
          type: "distributor",
          id: order.distributor.id,
        });
        const orderEntity = new CartApiMetadataEntity({
          type: "order",
          id: order.id,
          // Submit response does not include as much data
          // We need distributor reasons here
          isAddOnOrder: isAddOnOrder(cartOrder),
          meetsOrderMinimum: meetsOrderMinimum(cartOrder),
        });

        let entities = [orderGroupEntity, distributorEntity, cartProductsEntity, orderEntity];

        trackEvent("order_submitted", entities);
      }
    }

    let ordersEntity = new CartApiMetadataEntity({
      type: "orders",
      order_ids: submittedCart.orders.map(order => order.id),
    });

    if (individualOrder) {
      trackEvent("individual_order_submitted", [orderGroupEntity, ordersEntity]);
    } else {
      trackEvent("full_order_submitted", [orderGroupEntity, ordersEntity]);
    }

    $rootScope.cart = {};
    CartStore.update(n => {});
    CartApiCallbacks.prepInventory();
    $rootScope.cartTotal = null;
    Retailers.load();
    CartSuccessStore.set({ orderGroup: submittedCart });
    window["stateService"].go("layout.order_success");
  };

  const saveNote = function (orderId, { orderNotes, backupNotes }) {
    return $http({
      method: "PUT",
      url: "/api/retailer/orders/" + orderId,
      data: {
        order: {
          retailer_notes: orderNotes,
          backup_notes: backupNotes,
        },
      },
    });
  };

  const getFulfillmentInformation = async function (callback) {
    const response = await $http({
      method: "GET",
      url: "/api/retailer/cart/fulfillment_information_by_distributor",
    });
    if (callback) return callback(response);
  };

  const getTabcSpiritDeliveryMinimum = function (orderId) {
    return $http({
      method: "GET",
      url: "/api/retailer/orders/" + orderId + "/spirit_delivery_gallons_required",
    });
  };

  const getMinimumOrderQuantity = function (orderId) {
    return $http({
      method: "GET",
      url: "/api/retailer/orders/" + orderId + "/minimum_order_quantity",
    });
  };

  return {
    batchUpdateOrderWarehouses,
    batchUpdateOrders,
    submitIndividualOrder,
    remove,
    add,
    updateQuantity,
    submitForApproval,
    submitForPunchout,
    loadCount,
    load: loadCart,
    submit,
    saveNote,
    updateDeliveryOrPickupLocation,
    getFulfillmentInformation,
  };
};

export default service;
